import {
    FC, ReactNode, useState, useEffect,
} from 'react';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';

import { useWorkspaceStore } from '../../../../store/workspace.store';
import { ICoverage } from '../../../../interfaces/coverage.interface';
import {
    getCoverages,
    createCoverage,
    updateCoverage,
    deleteCoverage,
} from '../../../../api/coverages.api';
import { CoverageContext } from './coverage.ctx';

const CoverageProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { t } = useTranslation('settings', { keyPrefix: 'SETTINGS.COVERAGE' });

    const workspace = useWorkspaceStore((state) => state.data);

    const [coverages, setCoverages] = useState<ICoverage[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<ICoverage | null>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [pendingPolygon, setPendingPolygon] = useState<number[][] | null>(null);

    const fetchCoverages = () => {
        if (!workspace?.keyName) return;

        setLoading(true);
        getCoverages(workspace.keyName)
            .then((response: any) => {
                const data = response?.data;
                const active = Array.isArray(data) ? data.filter((c: ICoverage) => c.isActive) : [];
                setCoverages(active);
            })
            .catch(() => {
                notification.error({ message: t('FETCH_ERROR') });
            })
            .finally(() => setLoading(false));
    };

    const handleDrawComplete = (polygon: number[][]) => {
        setPendingPolygon(polygon);
        setIsDrawing(false);
    };

    const confirmCreate = (formData: { name: string; paymentMethod: string[]; carName: string[]; isVisible: boolean }) => {
        if (!workspace?.keyName || !pendingPolygon) return;

        createCoverage({
            keyName: workspace.keyName,
            polygon: pendingPolygon,
            name: formData.name,
            isVisible: formData.isVisible,
            paymentMethod: formData.paymentMethod,
            carName: formData.carName,
        })
            .then((response: any) => {
                const data = response?.data || response;
                setCoverages((prev) => [...prev, data]);
                setSelected(data);
                setPendingPolygon(null);
                notification.success({ message: t('CREATE_SUCCESS') });
            })
            .catch(() => {
                notification.error({ message: t('CREATE_ERROR') });
            });
    };

    const cancelCreate = () => {
        setPendingPolygon(null);
    };

    const handleUpdate = (id: string, data: Partial<ICoverage>) => {
        updateCoverage(id, data)
            .then((response: any) => {
                const updated = response?.data || response;
                setCoverages((prev) => prev.map((c) => (c._id === id ? { ...c, ...updated } : c)));
                if (selected?._id === id) setSelected({ ...selected, ...updated });
                notification.success({ message: t('UPDATE_SUCCESS') });
            })
            .catch(() => {
                notification.error({ message: t('UPDATE_ERROR') });
            });
    };

    const handleDelete = (id: string) => {
        deleteCoverage(id)
            .then(() => {
                setCoverages((prev) => prev.filter((c) => c._id !== id));
                if (selected?._id === id) setSelected(null);
                notification.success({ message: t('DELETE_SUCCESS') });
            })
            .catch(() => {
                notification.error({ message: t('DELETE_ERROR') });
            });
    };

    const handleToggleVisibility = (coverage: ICoverage) => {
        handleUpdate(coverage._id, { isVisible: !coverage.isVisible });
    };

    useEffect(() => {
        fetchCoverages();
    }, [workspace?.keyName]);

    return (
        <CoverageContext.Provider
            value={{
                coverages,
                loading,
                selected,
                isDrawing,
                pendingPolygon,
                setSelected,
                setIsDrawing,
                fetchCoverages,
                handleDrawComplete,
                confirmCreate,
                cancelCreate,
                handleUpdate,
                handleDelete,
                handleToggleVisibility,
            }}
        >
            {children}
        </CoverageContext.Provider>
    );
};

export { CoverageProvider };
